import { Box, styled } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
    ClaimControl,
    DelegateMenuButton,
    DelegationsHistory,
    ItemDetails,
    Loader,
    ProcessSteps,
} from 'components'
import {
    useAccessRequestPolicies,
    useApplicationSettings,
    useCancelRequest,
    useCurrentPerson,
    useRefreshRequestRisks,
    useRequestApprovalSteps,
    useRequestDelegationsHistory,
    useRequestRisks,
} from 'hooks'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Overview from './Overview'
import Approvers from './Approvers'
import RequestComments from './RequestComments'
import RequestItemsSummary from './RequestItemsSummary'
import useSubcomponents from 'useSubcomponents'
import { isNilOrEmpty } from 'packages/core'
import { smallScreenWidth } from 'utils'
import { RiskIndicator } from 'packages/eid-ui'
import { CancelationButton } from 'components/Button/CancelationButton'
import { RequestApprovalDecisionPrompt } from './RequestApprovalDecisionPrompt'
import RequestToDoTabs from './RequestToDoList'
import { WorkflowDecisionModal } from 'components/WorkflowDecisionModal'
import { RefreshButton } from 'components/Button/RefreshButton'
import { RiskViolationsTabLabel } from './RiskViolationsTabLabel'
import { ViolationsByItems } from './ViolationsByItems'
import { RisksWithViolations } from 'components/V2/RisksWithViolations'
import { RisksWithViolationsByFunctions } from 'components/V2/RiskViolationsByFunctions'

const TabsContainerBox = styled(Box)({
    backgroundColor: '#fbfbfd',
    borderBottom: '1px solid #d8dadd',

    paddingRight: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    overflow: 'auto',

    '& .Tabs-root': {
        display: 'flex',
    },

    [`@media (max-width:${smallScreenWidth})`]: {
        flexDirection: 'column-reverse',
        paddingTop: '20px',
        paddingLeft: '20px',
        paddingRight: '20px',

        '& .Tabs-root': {
            marginLeft: '-20px',
        },
    },
})

const OverflowContainer = styled(Box)({
    [`@media (max-width:${smallScreenWidth})`]: {
        overflowX: 'auto',
    },
})

const useStyles = makeStyles({
    subHeader: {
        position: 'sticky',
        top: '0px',
        zIndex: 200,
    },
    subHeaderContent: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        padding: '7px 20px',
        display: 'flex',
        alignItems: 'center',
    },
    riskIndicator: {
        fontWeight: 'bold',
    },
})

const RequestDetails = ({ data, header }) => {
    const { t } = useTranslation()
    const { data: requestAccessPolicies } = useAccessRequestPolicies()

    const { data: appConfig } = useApplicationSettings()
    const classes = useStyles()

    const { data: currentPerson } = useCurrentPerson()

    const [activeTab, setActiveTab] = useState('overview')

    const {
        hasAccessToDelegationsHistoryControl,
        hasAccessToRequestProcessStepsControl,
        hasAccessToRequestCurrentStepApproversControl,
        hasAccessToRequestClaimControl,
        hasAccessToRequestDelegateControl,
        hasAccessToAdminCancelRequestControl,
        hasAccessToCancelRequestControl,
        hasAccessToRefreshViolationsControl,
        canSeeRiskViolationsByItems,
        canSeeRiskViolationsByRisks,
        canSeeRiskViolationsByFunctions,
    } = useSubcomponents()

    let defaultSelected

    if (canSeeRiskViolationsByItems) defaultSelected = 'ByItems'
    if (canSeeRiskViolationsByRisks) defaultSelected = 'ByRisks'
    if (canSeeRiskViolationsByFunctions) defaultSelected = 'ByRisksFunctions'

    const [selected, setSelected] = useState(defaultSelected)

    const isClaimedOrAssigned = !isNilOrEmpty(data.claimedApproverId)

    const isClaimedOrClaimable =
        data.isGlobalStep && isNilOrEmpty(data.assignedByPersonId)

    const isBrNotActionable =
        data.status === 'Expired' || data.status === 'Canceled'

    const showRequestDecisionButtons =
        data && data.isApprovable && !isBrNotActionable

    const showRequestDecisionWorkflow =
        showRequestDecisionButtons && data.decisionWorkflowName ? true : false

    const hideToDoForGlobalWorkflow =
        showRequestDecisionWorkflow && data.isGlobalStep

    const isDelegatable = data.isGlobalStep

    const [cancelRequest, { isLoading: isCanceling }] = useCancelRequest(
        data.id,
    )
    const [
        refreshRequestRisks,
        { isLoading: isRefreshing },
    ] = useRefreshRequestRisks(data.id)
    if (!requestAccessPolicies) {
        return <Loader />
    }
    return (
        <>
            {header}

            <Box
                className={classes.subHeader}
                style={{
                    backgroundImage: appConfig.style.colors.primary?.gradient,
                    backgroundColor: appConfig.style.colors.primary?.header,
                }}
            >
                <Box className={classes.subHeaderContent}>
                    {hasAccessToRefreshViolationsControl && (
                        <Box
                            data-protectedsubcomponent={
                                hasAccessToRefreshViolationsControl
                            }
                        >
                            <RefreshButton
                                onClick={refreshRequestRisks}
                                isRefreshing={isRefreshing}
                            />
                        </Box>
                    )}

                    {data.riskLevel && (
                        <Box marginLeft={'15px'}>
                            <RiskIndicator
                                variant={data.riskLevel}
                                label={t(`${data.riskLevel}Risk`)}
                                className={classes.riskIndicator}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            <TabsContainerBox>
                <Box marginLeft="-13px" className="Tabs-root">
                    <ItemDetails.Tabs
                        variant="scrollable"
                        scrollButtons
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                    >
                        <ItemDetails.Tabs.Tab
                            value="overview"
                            label={t('Overview')}
                        />
                        {hasAccessToRequestProcessStepsControl && (
                            <ItemDetails.Tabs.Tab
                                value="processSteps"
                                label={t('ProcessSteps')}
                                data-protectedsubcomponent={
                                    hasAccessToRequestProcessStepsControl
                                }
                            />
                        )}
                        {hasAccessToRequestCurrentStepApproversControl &&
                            !isClaimedOrAssigned &&
                            data.approverCount !== null &&
                            data.currentApprovalStepId && (
                                <ItemDetails.Tabs.Tab
                                    value="currentStepApprovers"
                                    label={t('CurrentStepApprovers')}
                                    data-protectedsubcomponent={
                                        hasAccessToRequestCurrentStepApproversControl
                                    }
                                />
                            )}
                        {hasAccessToDelegationsHistoryControl && (
                            <ItemDetails.Tabs.Tab
                                value="delegationsHistory"
                                label={t('Delegations')}
                                data-protectedsubcomponent={
                                    hasAccessToDelegationsHistoryControl
                                }
                            />
                        )}
                        {data.riskCount > 0 &&
                            (canSeeRiskViolationsByItems ||
                                canSeeRiskViolationsByRisks ||
                                canSeeRiskViolationsByFunctions) && (
                                <ItemDetails.Tabs.Tab
                                    value={'riskViolations'}
                                    label={
                                        <RiskViolationsTabLabel
                                            selected={selected}
                                            setSelected={setSelected}
                                        />
                                    }
                                    data-protectedsubcomponent={`${canSeeRiskViolationsByItems} OR ${canSeeRiskViolationsByRisks} OR ${canSeeRiskViolationsByFunctions}`}
                                />
                            )}
                    </ItemDetails.Tabs>
                </Box>
                <Box display="flex" alignItems="center">
                    {hasAccessToRequestClaimControl &&
                        isClaimedOrClaimable &&
                        data.allowAssignApprover && (
                            <Box
                                marginRight="8px"
                                data-protectedsubcomponent={
                                    hasAccessToRequestClaimControl
                                }
                            >
                                <ClaimControl
                                    disabled={isBrNotActionable}
                                    page="Requests"
                                    isApprover={data.isApprover}
                                    requestId={data.id}
                                    currentApprovalStepId={
                                        data.currentApprovalStepId
                                    }
                                    claimedApproverId={data.claimedApproverId}
                                    claimedApproverFriendlyName={
                                        data.claimedApproverFriendlyName
                                    }
                                />
                            </Box>
                        )}
                    {hasAccessToRequestDelegateControl && isDelegatable && (
                        <DelegateMenuButton
                            page="Requests"
                            disabled={isBrNotActionable}
                            requestId={data.id}
                            isApprover={data.isApprover}
                            currentApprovalStepId={data.currentApprovalStepId}
                            assignedByPersonId={data.assignedByPersonId}
                            assignedByPersonFriendlyName={
                                data.assignedByPersonFriendlyName
                            }
                            claimedApproverId={data.claimedApproverId}
                            claimedApproverFriendlyName={
                                data.claimedApproverFriendlyName
                            }
                            allowAddApprover={data.allowAddApprover}
                            allowAssignApprover={data.allowAssignApprover}
                            protectSubcomponent={
                                hasAccessToRequestDelegateControl
                            }
                        />
                    )}
                </Box>
            </TabsContainerBox>
            <div style={{ marginBottom: '30px' }}>
                {activeTab === 'overview' && <Overview data={data} />}
                {activeTab === 'processSteps' && (
                    <OverflowContainer>
                        <ProcessSteps
                            useDataHook={useRequestApprovalSteps}
                            requestId={data.id}
                            isCanceled={data.status === 'Canceled'}
                        />
                    </OverflowContainer>
                )}
                {activeTab === 'currentStepApprovers' && (
                    <Approvers id={data.id} />
                )}
                {activeTab === 'delegationsHistory' && (
                    <OverflowContainer>
                        <DelegationsHistory
                            page="Request"
                            useDataHook={useRequestDelegationsHistory}
                            requestId={data.id}
                        />
                    </OverflowContainer>
                )}
                {activeTab === 'riskViolations' && (
                    <Fragment>
                        {selected === 'ByRisksFunctions' && (
                            <RisksWithViolationsByFunctions
                                requestType="businessRequests"
                                showDecisions={false}
                                requestId={data.id}
                            />
                        )}
                        {selected === 'ByItems' && (
                            <ViolationsByItems requestId={data.id} />
                        )}
                        {selected === 'ByRisks' && (
                            <RisksWithViolations
                                page={'Requests'}
                                requestId={data.id}
                                requestIdOrItemId={data.id}
                                useDataHook={useRequestRisks}
                            />
                        )}
                    </Fragment>
                )}
            </div>

            {!hideToDoForGlobalWorkflow ? (
                <RequestToDoTabs data={data} />
            ) : (
                <></>
            )}

            {showRequestDecisionButtons &&
                (showRequestDecisionWorkflow ? (
                    <Box
                        marginY={'24px'}
                        marginX={'28px'}
                        display={'flex'}
                        justifyContent={'flex-end'}
                    >
                        <WorkflowDecisionModal
                            fullScreen={true}
                            iframeHeight="calc(100vh - 7rem)"
                            page={'Requests'}
                            requestId={data.id}
                            currentApprovalStepId={data.currentApprovalStepId}
                            decisionWorkflowName={data.decisionWorkflowName}
                            maxWidth={'lg'}
                        />
                    </Box>
                ) : (
                    <RequestApprovalDecisionPrompt
                        businessRequest={data}
                        id={data.id}
                    />
                ))}
            <Box width="100%" overflow="auto">
                <RequestItemsSummary businessRequest={data} id={data.id} />
            </Box>

            <Box marginY="18px">
                <RequestComments requestId={data.id} businessRequest={data} />
            </Box>

            {data.isCancellable &&
                (hasAccessToAdminCancelRequestControl ||
                    (hasAccessToCancelRequestControl &&
                        data.initiatorPersonId === currentPerson.id)) && (
                    <Box
                        marginY={'32px'}
                        marginX={'32px'}
                        data-protectedsubcomponent={`${hasAccessToAdminCancelRequestControl} OR ${hasAccessToCancelRequestControl}`}
                    >
                        <CancelationButton
                            confirmationMessage={t(
                                'RequestCancelationConfirmation',
                            )}
                            isCanceling={isCanceling}
                            onClick={cancelRequest}
                        >
                            {t('CancelRequest')}
                        </CancelationButton>
                    </Box>
                )}
        </>
    )
}

export default RequestDetails
